import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2, Lock, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
  if (!detail) return "Etwas ist schiefgelaufen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const { user, refreshUser } = useAuth();
  const { lang, setLang, languages } = useLanguage();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // Force-change-password mode
  const [mustChange, setMustChange] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");

  useEffect(() => {
    if (user && !user.must_change_password) navigate("/", { replace: true });
    if (user && user.must_change_password) setMustChange(true);
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (refreshUser) await refreshUser();
      if (res.data?.must_change_password) {
        setMustChange(true);
      } else {
        navigate("/", { replace: true });
      }
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
    setSubmitting(false);
  };

  const submitChange = async (e) => {
    e.preventDefault();
    setErr(null);
    if (newPw.length < 8) { setErr("Mind. 8 Zeichen"); return; }
    if (newPw !== newPw2) { setErr("Passwoerter stimmen nicht ueberein"); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: password, new_password: newPw },
        { withCredentials: true }
      );
      if (refreshUser) await refreshUser();
      navigate("/", { replace: true });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB]" data-testid="login-page">
      <div className="w-full max-w-md px-8">
        <div className="flex items-center gap-3 mb-12 justify-center">
          <img src="/invictaflow-logo.png" alt="InvictaFlow" className="w-12 h-12 rounded-xl" />
          <div className="leading-tight">
            <h1 className="text-3xl font-bold text-slate-900 font-heading tracking-tight">
              InvictaFlow
            </h1>
            <p className="text-slate-400 text-[10px] tracking-[0.3em] uppercase">InvictaX Group GmbH</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-8">
          {!mustChange ? (
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-1 text-center font-heading">
                  Willkommen zurueck
                </h2>
                <p className="text-slate-500 text-sm text-center mb-6">
                  Melde dich mit deinen Zugangsdaten an
                </p>
              </div>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-Mail"
                  className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
                  data-testid="login-email"
                />
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Passwort"
                  className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
                  data-testid="login-password"
                />
              </div>
              {err && (
                <p className="text-red-400 text-xs text-center" data-testid="login-error">{err}</p>
              )}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium text-base rounded-xl"
                data-testid="login-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Anmelden
              </Button>
              <p className="text-slate-400 text-xs text-center pt-2">
                Noch kein Konto?{" "}
                <Link to="/registrieren" className="text-[#2563EB] underline" data-testid="login-register-link">
                  Jetzt kostenlos registrieren
                </Link>
              </p>
            </form>
          ) : (
            <form onSubmit={submitChange} className="space-y-4" data-testid="change-pw-form">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-1 text-center font-heading">
                  Passwort aendern
                </h2>
                <p className="text-slate-500 text-sm text-center mb-6">
                  Beim ersten Login musst du dein Initial-Passwort durch ein eigenes ersetzen.
                </p>
              </div>
              <Input
                type="password"
                required
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="Neues Passwort (mind. 8 Zeichen)"
                className="h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
                data-testid="new-password"
              />
              <Input
                type="password"
                required
                value={newPw2}
                onChange={(e) => setNewPw2(e.target.value)}
                placeholder="Neues Passwort wiederholen"
                className="h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
                data-testid="new-password-2"
              />
              {err && <p className="text-red-400 text-xs text-center">{err}</p>}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium text-base rounded-xl"
                data-testid="change-pw-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Passwort speichern
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-slate-400 text-xs mt-8">
          InvictaFlow Marketing — InvictaX Group GmbH &copy; 2026
        </p>

        <div className="flex justify-center gap-2 mt-4">
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              data-testid={`login-lang-${l.code}`}
              className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                lang === l.code
                  ? "bg-[#2563EB]/10 text-[#2563EB] font-medium"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {l.flag}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
